import {
  alpha,
  Avatar,
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import ThemeManager from 'utils/ThemeManager';
import SearchIcon from '@mui/icons-material/Search';
import { useRootStore } from 'hooks/useRootStore';
import { observer } from 'mobx-react-lite';
import { NavbarSearchResultT } from 'stores/root/NavbarSearchStore';
import { StudentT } from 'types/shared/core/Student';
import { TeacherWithDescT } from 'types/frontend/shared';
import { ReactNode, useCallback } from 'react';

import ClearIcon from '@mui/icons-material/Clear';

function HighlightKeyword({
  text,
  keyword,
}: {
  text: string;
  keyword: string;
}) {
  // Ensure text is a string
  if (typeof text !== 'string') {
    text = String(text);
  }

  const { palette } = useTheme();

  // Split the text into segments and highlight the keyword
  const segments: ReactNode[] = [];

  if (keyword.length < 2) {
    segments.push(text);
  } else {
    // Create a case-insensitive regex to match the keyword
    const regex = new RegExp(`(${keyword})`, 'gi');

    let match;
    let lastIndex = 0;

    while ((match = regex.exec(text)) !== null) {
      // Add the text part before the keyword
      segments.push(text.substring(lastIndex, match.index));

      // Add the highlighted keyword
      segments.push(
        <span key={match.index} style={{ color: palette.primary.main }}>
          {match[0]}
        </span>,
      );

      lastIndex = regex.lastIndex;
    }

    // Add any remaining text after the last match
    segments.push(text.substring(lastIndex));
  }

  return (
    <Typography variant="body1" color="textSecondary">
      {segments}
    </Typography>
  );
}

function NavbarSearchResult({
  keyword,
  result,
  isSelected,
  index,
  onMouseOver: handleMouseOver,
  onClick: handleClick,
}: {
  keyword: string;
  result: NavbarSearchResultT;
  isSelected: boolean;
  index: number;
  onMouseOver: (i: number) => void;
  onClick: (i: number, withModifierKey: boolean) => void;
}) {
  const { palette } = useTheme();

  let profileUrl = '';
  let name = '';

  if (result.category === 'student') {
    const student = result.item as StudentT;
    profileUrl = student.profileUrl || '';
    name = student.name;
  }
  if (result.category === 'teacher') {
    const teacher = result.item as TeacherWithDescT;
    profileUrl = teacher.profileUrl || '';
    name = teacher.name;
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      px={2}
      py={0.5}
      sx={{
        display: 'flex',
        backgroundColor: isSelected ? palette.action.hover : undefined,
        cursor: 'pointer',
      }}
      onClick={(e) =>
        handleClick(index, e.shiftKey || e.ctrlKey || e.altKey || e.metaKey)
      }
      onMouseOver={() => handleMouseOver(index)}
    >
      <Avatar
        src={profileUrl || undefined}
        sx={{
          height: 32,
          width: 32,
          flex: '32px 0',
        }}
      />
      <Typography variant="subtitle1">{name}</Typography>
      <Typography variant="body1" color="textSecondary">
        {result.foundInProperty} :
      </Typography>
      <HighlightKeyword text={result.keywordContext} keyword={keyword} />
    </Stack>
  );
}

const NavbarSearchArea = observer(() => {
  const { navbarSearchStore } = useRootStore();

  const {
    handleFocus,
    searchKeyword,
    setSearchKeyword,
    results,
    selectedResultIndex,
    moveSelectedItemIndexLazily,
    handleEnterKey,
    handleMouseOver,
    handleResultClick,
  } = navbarSearchStore;

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        moveSelectedItemIndexLazily('up');
        console.log('Up key pressed');
        break;
      case 'ArrowDown':
        e.preventDefault();
        moveSelectedItemIndexLazily('down');
        console.log('Down key pressed', e);
        break;
      case 'Enter':
        e.preventDefault();
        handleEnterKey(e.shiftKey || e.ctrlKey || e.altKey || e.metaKey);
        break;
      default:
        break;
    }
  }, []);

  const categories = [
    ['student', '학생'],
    ['teacher', '교사'],
    ['cls', '클래스'],
  ];

  return (
    <Box width={180} mr={1}>
      <TextField
        name="searchAnything"
        size="small"
        variant="outlined"
        placeholder="뭔가 찾기"
        autoComplete="off"
        value={searchKeyword}
        onChange={(e) => setSearchKeyword(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: searchKeyword.length ? (
            <IconButton size="small" onClick={() => setSearchKeyword('')}>
              <ClearIcon />
            </IconButton>
          ) : null,
        }}
      />

      {results.length ? (
        <Box
          position="fixed"
          width={360}
          zIndex="modal"
          sx={{
            'top': '68px',
            'right': '4px',
            'borderRadius': '8px',
            'backgroundColor': 'background.paper',
            'boxShadow': (theme) => theme.shadows[8],

            'overflow': 'hidden',
            'overflowY': 'auto',
            '&::-webkit-scrollbar': {
              width: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(ThemeManager.getNeutral(6), 0.1),
              borderRadius: '10px',
            },

            'maxHeight': `calc(100vh - 72px)`, // 상단과 하단에서 4px 여백을 주고 그 안에서 작동
          }}
        >
          <Stack>
            {categories.map((category) => {
              const [key, name] = category;
              if (!results.some((item) => item.category === key)) return null;

              return (
                <Stack key={key} py={2}>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    px={2}
                    pb={0.5}
                  >
                    {`${name} (${
                      results.filter((item) => item.category === key).length
                    })`}
                  </Typography>
                  {results.map((result, index) =>
                    result.category === key ? (
                      <NavbarSearchResult
                        key={`${index}`}
                        index={index}
                        keyword={searchKeyword}
                        result={result}
                        isSelected={index === selectedResultIndex}
                        onMouseOver={handleMouseOver}
                        onClick={handleResultClick}
                      />
                    ) : null,
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
});

export default NavbarSearchArea;
